import axios from "axios";
import notify from "devextreme/ui/notify";
import DataSource from "devextreme/data/data_source";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import {
    AppSettingsLocalStorageKey,
  ExcelExportDefaultFileName,
  ExcelExportDefaultSheetName
} from '../const/constants';
import { EMPTY_GUID } from '../enum/Enum';
import routes from '../assets/apiRoutes.json';

/** How long a downloaded report's blob URL is kept, so the browser can finish saving it. */
const REPORT_BLOB_URL_LIFETIME_MS = 60000;

/**
 * Downloads a generated report file under its own name.
 *
 * GetReportFile requires the bearer token, which a plain link or window.open cannot send, so the file
 * is fetched through axios with the caller's current token and saved from a blob URL. The token is
 * passed explicitly: the AppContainer interceptor keeps the token from the first render, which is stale
 * once it has been renewed, and the WebAPI reads the access_token header first.
 * It is saved rather than opened in a tab: a blob URL carries no file name, so a PDF opened in the
 * browser's viewer would save as the blob's GUID instead of the report name.
 *
 * Never rejects: a failure is reported to the user here. Resolves true once the file has been handed
 * to the browser and false otherwise, so a caller can keep its loader up until then.
 */
export async function downloadReportFile(fileName, accessToken) {
    if (!fileName) return false;

    try {
        const res = await axios.get(`${window._env_.REACT_APP_APIS}/${routes.reports.GetReportFile}`, {
            params: { fileName },
            responseType: "blob",
            headers: { access_token: accessToken },
        });

        const blob = new Blob([res.data], { type: res.headers?.["content-type"] || res.data.type });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();

        setTimeout(() => window.URL.revokeObjectURL(url), REPORT_BLOB_URL_LIFETIME_MS);
        return true;
    } catch (err) {
        if (err?.response?.status === 401) return false; // the axios interceptor already redirects
        console.error("Report file download failed", err);
        notify({ message: "The report file could not be downloaded.", width: 320 }, "error", 4000);
        return false;
    }
}

/**
 * Click handler for HTML that may contain GetReportFile links (AI chat answers). Such a link can't
 * carry the bearer token, so the click is taken over and the file is downloaded via downloadReportFile.
 */
export function handleReportFileLinkClick(e, accessToken) {
    const anchor = e.target?.closest?.("a");
    if (!anchor || !anchor.href) return;

    let url;
    try {
        url = new URL(anchor.href, window.location.href);
    } catch {
        return;
    }

    if (!url.pathname.toLowerCase().endsWith(`/${routes.reports.GetReportFile}`.toLowerCase())) return;

    e.preventDefault();
    downloadReportFile(url.searchParams.get("fileName"), accessToken);
}

/** True when the value is a real Guid: not null/blank and not the empty Guid (case-insensitive). */
export function isValidGuid(value) {
    if (value == null || value === "") return false;
    return String(value).toLowerCase() !== EMPTY_GUID;
}

export function GetMinutesFromDate(date) {
    var d = new Date(date),
        minutes = '' + d.getMinutes();
    if (minutes.length < 2)
        minutes = '0' + minutes;
    return minutes;    
}
export function GetHoursFromDate(date) {
    var d = new Date(date),
        hours = '' + (d.getHours());
    hours = (hours % 12 == 0) ? '12' : '' + hours % 12;
    if (hours.length < 2)
        hours = '0' + hours;
    return hours;
}
export function GetDatePartOnly(date, joinArrWith = '-') {
    var d = new Date(date),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear();
    if (month.length < 2)
        month = '0' + month;
    if (day.length < 2)
        day = '0' + day;
    return [year, month, day].join(joinArrWith);
}
export function GetDatePartOnlyDiffFormat(date, joinArrWith = '-') {
    var d = new Date(date),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear();
    if (month.length < 2)
        month = '0' + month;
    if (day.length < 2)
        day = '0' + day;
    return [month, day, year].join(joinArrWith);
}
export function GetTimePartOnly(date) {
    var d = new Date(date),
        hours = '' + (d.getHours()),
        minutes = '' + d.getMinutes();
    hours = (hours % 12 == 0) ? '12' : '' + hours % 12;
    if (hours.length < 2)
        hours = '0' + hours;
    return `${hours}:${minutes.length < 2 ? '0' + minutes : minutes} ${(d.getHours() < 12) ? "AM" : "PM"}`;
}
export function GetDateTimeStandardFormat(date) {
    return GetDatePartOnlyDiffFormat(date, '/') + ' ' + GetTimePartOnly(date);
}

export function GetFormatedDateWithTime(d) { // yyyy-MM-ddTHH:mm:ss
    if (d == null || d == '') {
        return null; // or return null; or any other default value
    }
    var date = new Date(d);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const formatedDate = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    return formatedDate;
}
export function FillFilters(fields){
    const filters = [];
    for (const [key, value] of Object.entries(fields)) {
        if (value === '' || value === null || value === undefined) {
            continue;
        }
      if (filters.length > 0) {
            filters.push("and");
        }
        filters.push([key, "contains", value]);
    }
    return filters;
};
export function buildDxFilters(valuesObj, layout,excludeFields=[]) {
    let filterTypes = {};
    let isObj=isObject(layout);
    if (!isObj && layout) {
        layout.forEach(group => {
            group.fields.forEach(field => {
                filterTypes[field.name] =field.filterType?? field.type;
            });
        });
    }
    if(isObj){
     filterTypes =layout;
    }
    const filters = [];

    for (const [key, value] of Object.entries(valuesObj)) {
        let excludedField= excludeFields.find(field => field===key);
        if (value === '' || value === null || value === undefined || !key || excludedField) continue;

        const type = filterTypes[key] || 'text';

        if (filters.length > 0) filters.push('and');

        switch (type) {
                 case 'rangeFrom': {
                filters.push([
                    [key, '>=', value]
                ]);
                break;
            }
               case 'rangeTo': {
                filters.push([
                    [key, '<=', value]
                ]);
                break;
            }
            case 'datetime': {
                const date = new Date(value);
                const nextMinute = new Date(date.getTime() + 60 * 1000);

                filters.push([
                    [key, '>=', date],
                    'and',
                    [key, '<', nextMinute]
                ]);
                break;
            }
            case 'date': {
                const date = new Date(value);
                filters.push([
                    [key, '<=', date]
                ]);
                break;
            }
            case 'lookup':
                filters.push([key, '=', value]);
                break;
            case 'select':
                filters.push([key, '=', value]);
                break;
            case 'number':
                filters.push([key, '=', Number(value)]);
                break;

            case 'boolean':
                filters.push([key, '=', Boolean(value)]);
                break;

            default: // text or other
                filters.push([key, 'contains', value]);
                break;
        }
    }

    return filters;
}
function isObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function FormatPhoneNumber(input) {
    if(!input || input==="" || input ===null || input === undefined){
        return input;
    }
  // Case 1: X(XXX)XXX... + optional ext (with or without hyphen)
  const matchGeneral = input.match(/^(?:(\d*)\((\d*)\))?(\d*)(?:,\s*(\d+))?$/);

  if (matchGeneral) {
    const [_, prefix, areaCode, mainNumber, extension] = matchGeneral;
    
    // Format main number: Add hyphen after 3 digits if length > 3
    const formattedMain = mainNumber.length > 3 
      ? `${mainNumber.slice(0, 3)}-${mainNumber.slice(3)}` 
      : mainNumber;

    // Construct final number
    let result = prefix ? `+${prefix}` : '';
    if(areaCode==="" || areaCode ===undefined || areaCode ===null)
       result += `${formattedMain}`;
    else
    result += ` (${areaCode}) ${formattedMain}`;
    
    if (extension) result += ` ext: ${extension.trim()}`;

    return result;
  }

  return input; // Fallback for non-matching formats
}
    export function  convertToCustomPhoneFormat(str) {
        if (!str) return '';
        str = str.padEnd(25, ' '); // or total of 4+6+10+7 = 25
        const part1 = str.slice(0, 4).trim();
        const part2 = str.slice(4, 10).trim();
        const part3 = str.slice(10, 20).trim();
        const part4 = str.slice(20, 27).trim();
        if(part3 ==="" && part4 ===""){return ""}
        let formatted=`${part1.trim()}(${part2.trim()})${part3.trim()},${part4.trim()}`;
        if(formatted){
           formatted = formatted.replace(/\(\)/g, "").replace(/,$/, "");
        }
        formatted= formatted.replace(/\s/g,'');
        return formatted;
          };
    export function loadLookupFromDataSource(accessToken,formFields,formRefs,name, url,valueExpr='id',displayExpr='name',loadParams=null) {
          var dataSource = new DataSource({
              store: new createStore({
                  key: valueExpr,
                  loadUrl: url,
                  loadParams:loadParams,
                  onBeforeSend: function (method, ajaxOptions) {
                      ajaxOptions.headers = {
                          access_token: accessToken,
                          Authorization: `Bearer ${accessToken}`
                      };
                  }
              }),
              sort: [{ selector: displayExpr, desc: false }],
              paginate: true,
              pageSize: 100
          });
          if (formRefs && formRefs.current && formRefs.current[name]) {
              const ref = formRefs.current[name];
              if (typeof ref.instance === 'function') {
                  const instance = ref.instance();
                  if (instance && typeof instance.option === 'function') {
                      instance.option('dataSource', dataSource);
                      instance.option('valueExpr', valueExpr);
                      instance.option('displayExpr', displayExpr);
                      if(formFields && formFields[name])
                          instance.option('value', formFields[name]);
                  }
              }
          }
    };
    export function loadLookupFromStaticDataSource(name,dataSource,formFields,formRefs,valueExpr="key",displayExpr="value") {
          if (formRefs && formRefs.current && formRefs.current[name]) {
              const ref = formRefs.current[name];
              if (typeof ref.instance === 'function') {
                  const instance = ref.instance();
                  if (instance && typeof instance.option === 'function') {
                      instance.option('dataSource', dataSource);
                      instance.option('valueExpr', valueExpr);
                      instance.option('displayExpr', displayExpr);
                      if(formFields && formFields[name])
                          instance.option('value', formFields[name]);
                  }
              }
          }
    };
    export function  combinedLayouts (layouts){
    return layouts.map(({ name, layout }) => {
        if (name !== 'PrimaryLayout') {
            return layout.map(group => ({
                ...group,
                label: "",
                fields: group.fields.map(field => ({
                    ...field,
                    visible: false
                }))
            })).flat();
        }
        return layout.flat();
    }).flat();
}
    export function DisableEnableControls (name,disabled,formRefs){
        if (formRefs && formRefs.current && formRefs.current[name]) {
            const ref = formRefs.current[name];
            if (typeof ref.instance === 'function') {
                const instance = ref.instance();
                if (instance && typeof instance.option === 'function') {
                    instance.option('disabled', disabled);
                }
            }
        }
    }
    export function ShowHideControls (name,visible,formRefs){
        if (formRefs && formRefs.current && formRefs.current[name]) {
            const ref = formRefs.current[name];
            if (typeof ref.instance === 'function') {
                const instance = ref.instance();
                if (instance && typeof instance.option === 'function') {
                    instance.option('visible', visible);
                }
            }
        }
    }
    export function SetControlValue (name,value,formRefs){
        if (formRefs && formRefs.current && formRefs.current[name]) {
            const ref = formRefs.current[name];
            if (typeof ref.instance === 'function') {
                const instance = ref.instance();
                if (instance && typeof instance.option === 'function') {
                    instance.option('value', value);
                }
            }
        }
    }
export function dateTimeToJulian(date) {
  const d = date instanceof Date ? date : new Date(date);

  const Y = d.getFullYear();
  const M = d.getMonth() + 1; // 1..12
  const D = d.getDate();

  const a = Math.floor((14 - M) / 12);
  const y = Y + 4800 - a;
  const m = M + 12 * a - 3;

  const JDN =
    D +
    Math.floor((153 * m + 2) / 5) +
    365 * y +
    Math.floor(y / 4) -
    Math.floor(y / 100) +
    Math.floor(y / 400) -
    32045;

  // Delphi gives *.5 at midnight → add -0.5
  return JDN - 0.5;
}
export function getKeyByValue(obj, value) {
    return Object.keys(obj).find(key => obj[key] === value);
}
    export function loadDatasource (accessToken,url,key,loadParams) {
        let store = createStore({
            key: key,
            loadUrl: url,
            loadParams: loadParams || undefined,
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        const dataSource = new DataSource({ store: store });
        return dataSource;
    };

    /**
     * Clears all chat messages for a specific user from localStorage
     * @param {string} userId - The user ID (sub from oidc user profile)
     */
    export function clearChatMessagesForUser(userId) {
        if (userId) {
            const storageKey = `chat_messages_${userId}`;
            localStorage.removeItem(storageKey);
        }
    }

    /**
     * Clears all chat messages for all users (useful for logout)
     * This removes all localStorage keys that start with "chat_messages_"
     */
    export function clearAllChatMessages() {
        // Get all localStorage keys
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('chat_messages_')) {
                keysToRemove.push(key);
            }
        }
        // Remove all chat message keys
        keysToRemove.forEach(key => localStorage.removeItem(key));
        
        // Also remove the old format key if it exists (for migration)
        localStorage.removeItem('chat_messages');
    }

/**
 * Global Excel export handler for DevExtreme DataGrid
 * 
 * @param {Object} e - Export event object from DevExtreme DataGrid
 * @param {string} fileName - Optional custom file name (default: ExcelExportDefaultFileName from constants)
 * @param {string} sheetName - Optional custom sheet name (default: ExcelExportDefaultSheetName from constants)
 */
export function handleDataGridExport(e, fileName = ExcelExportDefaultFileName, sheetName = ExcelExportDefaultSheetName) {
    try {
        // Use globally available libraries
        if (!window.ExcelJS || !window.saveAs || !window.excelExporter) {
            console.error('Excel export libraries are not available. Please ensure ExcelJS, file-saver, and devextreme/excel_exporter are loaded globally.');
            e.cancel = true;
            return;
        }
        
        const workbook = new window.ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(sheetName);
        
        window.excelExporter.exportDataGrid({
            component: e.component,
            worksheet: worksheet,
            autoFilterEnabled: true
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                window.saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName);
            });
        }).catch((error) => {
            console.error(error);
        });
        e.cancel = true;
    } catch (error) {
        console.error( error);
        e.cancel = true;
    }
}
    export function getAppSettings (){
        const storedSettings = localStorage.getItem(AppSettingsLocalStorageKey);
        if (storedSettings) {
            let parsedSettings = JSON.parse(storedSettings);
            parsedSettings.isDefault = parsedSettings.AppLogo === "../images/logo.png";
            return parsedSettings;
        }
    };
        export function onContentReady (e) {
        if (!e.component._hasLoaded) {
            e.component._hasLoaded = true;
            var selection = e.component.getSelectedRowKeys();
            if (selection.length == 0) {
                e.component.selectRowsByIndexes([0]);
            }
        }
    };
        export function onEditingStart (e,groupsGridRef) {
        if(e.key != null && e.key != undefined && !groupsGridRef.current.instance().isRowSelected(e.key))
        {
            groupsGridRef.current.instance().selectRows([e.key], false);
        }
    };

/**
 * Formats a quantity the way the Delphi screens do: two decimals, thousands separator, locale-aware.
 * Mirrors uabcurrencyUtils.GetNumberFormatStr(2, TRUE, TRUE) — the mask behind every ",0.00" DisplayFormat.
 * Non-numeric input formats as 0.00 rather than "NaN", matching the try/except fallback in the Delphi code.
 */
export function formatQuantity(value) {
    const num = Number(value);
    return (Number.isFinite(num) ? num : 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}
